import React from "react";
import { createPortal } from "react-dom";
import { getToken } from "./tokenStorage";
import { NavLink, useLocation } from "react-router-dom";
import { AuthCtx } from "./AuthProvider";
import { invalidateCachedJson } from "./apiCache";
import { useOfflineStatus, formatCacheTime } from "./useOfflineStatus";
import ConnectionIssueModal from "./ConnectionIssueModal";
import { useSwUpdate } from "./useSwUpdate";
import { useTheme } from "./useTheme";
import { HALLOWEEN_MODE } from "./halloweenMode";

const API = window.location.origin;
const ARCHIVE_IMPORT_JOB_KEY = "audassist.archiveImportJob";
const ARCHIVE_IMPORT_JOB_SNAPSHOT_KEY = "audassist.archiveImportJobSnapshot";
const ARCHIVE_IMPORT_FOCUS_KEY = "audassist.archiveImportFocus";

function ArchiveFileGlyph({ className = "h-5 w-5" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true">
      <path
        d="M7 2.75h6.9c.53 0 1.04.21 1.41.59l3.35 3.35c.38.37.59.88.59 1.41V19A2.25 2.25 0 0 1 17 21.25H7A2.25 2.25 0 0 1 4.75 19V5A2.25 2.25 0 0 1 7 2.75Z"
        fill="currentColor"
        opacity="0.14"
      />
      <path
        d="M14 2.75v3.5A1.75 1.75 0 0 0 15.75 8H19"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M8.25 11.25h7.5M8.25 14.25h7.5M8.25 17.25h5"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
      <path
        d="M6.75 3.5h7.1c.46 0 .9.18 1.23.51l3.41 3.41c.33.33.51.77.51 1.23V19a1.75 1.75 0 0 1-1.75 1.75H6.75A1.75 1.75 0 0 1 5 19V5.25c0-.97.78-1.75 1.75-1.75Z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <rect x="3.2" y="9.7" width="6.6" height="6.6" rx="1.6" fill="currentColor" />
      <path
        d="m4.8 11.2 1.7 3.6 1.7-3.6"
        stroke="white"
        strokeWidth="1.25"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function normalizeArchiveFocusPayload(job) {
  const rangeIds = Array.from(
    new Set(
      (Array.isArray(job?.archives) ? job.archives : [])
        .map((item) => String(item?.range_id || "").trim())
        .filter(Boolean)
    )
  );
  if (!job?.job_id || !job?.source || rangeIds.length === 0) return null;
  return {
    job_id: job.job_id,
    source: String(job.source || "").trim().toUpperCase(),
    range_ids: rangeIds,
    created_at: Date.now(),
  };
}

function findVisibleScheduleNavTarget() {
  if (typeof document === "undefined") return null;
  const nodes = Array.from(document.querySelectorAll('[data-nav-target="schedule"]'));
  const visibleNodes = nodes
    .map((node) => ({ node, rect: node.getBoundingClientRect() }))
    .filter(({ rect }) => rect.width > 0 && rect.height > 0);
  if (!visibleNodes.length) return null;
  visibleNodes.sort((left, right) => {
    const byArea = right.rect.width * right.rect.height - left.rect.width * left.rect.height;
    if (byArea !== 0) return byArea;
    return left.rect.top - right.rect.top;
  });
  return visibleNodes[0].node;
}

function archiveFlightTone(status) {
  if (status === "error") {
    return "border-rose-200/90 bg-[linear-gradient(180deg,rgba(255,255,255,0.98),rgba(255,241,242,0.96))] text-rose-500 ring-1 ring-rose-200/70 shadow-[0_20px_34px_-22px_rgba(244,63,94,0.45)]";
  }
  if (status === "success") {
    return "border-emerald-200/90 bg-[linear-gradient(180deg,rgba(255,255,255,0.98),rgba(236,253,245,0.96))] text-emerald-600 ring-1 ring-emerald-200/70 shadow-[0_20px_34px_-22px_rgba(5,150,105,0.7)]";
  }
  return "border-blue-200/90 bg-[linear-gradient(180deg,rgba(255,255,255,0.98),rgba(239,246,255,0.96))] text-blue-600 ring-1 ring-blue-200/70 shadow-[0_20px_34px_-22px_rgba(37,99,235,0.45)]";
}

function getAuthHeaders() {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function resolveAvatarSrc(avatarUrl) {
  if (!avatarUrl) return "";
  if (/^https?:\/\//i.test(avatarUrl) || avatarUrl.startsWith("data:")) {
    return avatarUrl;
  }
  return `${window.location.origin}${avatarUrl}`;
}

function normalizeArchiveSourceLabel(source) {
  const value = String(source || "").trim().toUpperCase();
  return value || "Архив";
}

const MENU_META = {
  "/": {
    title: "Панель",
    description: "Обзор и ключевые показатели",
    icon: "dashboard",
    tone: "from-amber-50 via-white to-yellow-50 text-amber-600 border-amber-100",
  },
  "/auditories": {
    title: "Аудитории",
    description: "Быстрый доступ к кабинетам",
    icon: "rooms",
    tone: "from-emerald-50 via-white to-teal-50 text-emerald-600 border-emerald-100",
  },
  "/support": {
    title: "Поддержка",
    description: "Заявки и сопровождение",
    icon: "support",
    tone: "from-slate-50 via-white to-sky-50 text-slate-700 border-slate-200",
  },
  "/schedule": {
    title: "Расписание",
    description: "Недели, группы и изменения",
    icon: "schedule",
    tone: "from-blue-50 via-white to-cyan-50 text-blue-600 border-blue-100",
  },
  "/teacher-export": {
    title: "Нагрузка",
    description: "Выгрузки и отчёты",
    icon: "export",
    tone: "from-rose-50 via-white to-orange-50 text-rose-600 border-rose-100",
  },
  "/account": {
    title: "Личный кабинет",
    description: "Профиль, брони и управление доступом",
    icon: "account",
    tone: "from-slate-50 via-white to-cyan-50 text-slate-700 border-slate-200",
  },
  "/users": {
    title: "Пользователи",
    description: "Список участников системы",
    icon: "users",
    tone: "from-indigo-50 via-white to-blue-50 text-indigo-600 border-indigo-100",
  },
};

function MenuGlyph({ name, className = "h-5 w-5" }) {
  const halloweenMask = HALLOWEEN_MODE ? {
    rooms:    "/castle_black.svg",
    schedule: "/scroll_black.svg",
    users:    "/icon-ghost.svg",
    export:   "/witch-hat.svg",
  }[name] : null;
  if (halloweenMask) {
    return (
      <span
        aria-hidden="true"
        className={className}
        style={{
          display: "inline-block",
          backgroundColor: "currentColor",
          WebkitMaskImage: `url('${halloweenMask}')`,
          maskImage: `url('${halloweenMask}')`,
          WebkitMaskRepeat: "no-repeat",
          maskRepeat: "no-repeat",
          WebkitMaskPosition: "center",
          maskPosition: "center",
          WebkitMaskSize: "contain",
          maskSize: "contain",
        }}
      />
    );
  }
  switch (name) {
    case "dashboard":
      return (
        <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M4 13h4v7H4z" />
          <path d="M10 4h4v16h-4z" />
          <path d="M16 9h4v11h-4z" />
        </svg>
      );
    case "rooms":
      return (
        <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M5 20h14" />
          <path d="M7 20V6.5A1.5 1.5 0 0 1 8.5 5h7A1.5 1.5 0 0 1 17 6.5V20" />
          <path d="M10 20v-5h4v5" />
          <path d="M12 10.5h.01" />
        </svg>
      );
    // Внутри switch (name) в компоненте MenuGlyph
    case "login":
      return (
        <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
          <polyline points="10 17 15 12 10 7" />
          <line x1="15" y1="12" x2="3" y2="12" />
        </svg>
      );
    case "support":
      return (
        <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M4 12a8 8 0 0 1 16 0" />
          <path d="M4 13v3a2 2 0 0 0 2 2h1v-5H6a2 2 0 0 0-2 2Z" />
          <path d="M20 13v3a2 2 0 0 1-2 2h-1v-5h1a2 2 0 0 1 2 2Z" />
          <path d="M12 20h2" />
          <path d="M14 20a2 2 0 0 0 2-2" />
        </svg>
      );
    case "schedule":
      return (
        <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="3" y="5" width="18" height="16" rx="3" />
          <path d="M16 3v4M8 3v4M3 10h18" />
        </svg>
      );
    case "export":
      return (
        <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M4 20h16" />
          <path d="M7 20v-6" />
          <path d="M12 20V8" />
          <path d="M17 20V4" />
        </svg>
      );
    case "feedback":
      return (
        <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M7.5 18.5H6a3 3 0 0 1-3-3V7a3 3 0 0 1 3-3h12a3 3 0 0 1 3 3v6.5a3 3 0 0 1-3 3h-3.5l-3.5 3v-3" />
        </svg>
      );
    case "account":
      return (
        <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M18 21a6 6 0 0 0-12 0" />
          <circle cx="12" cy="8" r="4" />
        </svg>
      );
    case "users":
      return (
        <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M17 21a5 5 0 0 0-10 0" />
          <circle cx="12" cy="8" r="4" />
          <path d="M23 21a5 5 0 0 0-7.5-4.33" />
          <path d="M1 21a5 5 0 0 1 7.5-4.33" />
        </svg>
      );
    case "logout":
      return (
        <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M14 4h4a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-4" />
          <path d="M10 8 6 12l4 4" />
          <path d="M6 12h9" />
          <path d="M4 4h7v16H4" />
        </svg>
      );
    default:
      return (
        <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
          <circle cx="12" cy="12" r="2" />
          <circle cx="19" cy="12" r="2" />
          <circle cx="5" cy="12" r="2" />
        </svg>
      );
  }
}

export default function Layout({ title, subtitle, wide = false, children }) {
  const location = useLocation();
  const { user, ready, logout } = React.useContext(AuthCtx);
  const [navCrownEquipped, setNavCrownEquipped] = React.useState(false);
  const [navIsCrownHolder, setNavIsCrownHolder] = React.useState(false);
  React.useEffect(() => {
    if (!ready || !user) return;
    const token = getToken();
    if (!token) return;
    fetch('/skrep/balance', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data) {
          setNavIsCrownHolder(!!data.is_crown_holder);
          setNavCrownEquipped(!!data.crown_equipped);
          window.__userIsCrownHolder = !!data.is_crown_holder;
        }
      })
      .catch(() => {});
  }, [ready, user?.id]);
  React.useEffect(() => {
    const h = (e) => {
      if (e.detail?.crown_transferred) { setNavIsCrownHolder(true); setNavCrownEquipped(true); window.__userIsCrownHolder = true; }
    };
    window.addEventListener('skrepCollected', h);
    return () => window.removeEventListener('skrepCollected', h);
  }, []);
  const { isOnline, cacheTs, cacheUsed } = useOfflineStatus();
  const { updateReady, reload } = useSwUpdate();
  const { isDark } = useTheme();
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [archiveImportJob, setArchiveImportJob] = React.useState(null);
  const [archiveImportHidden, setArchiveImportHidden] = React.useState(false);
  const [archiveImportClosing, setArchiveImportClosing] = React.useState(false);
  const [archiveDisplayProgress, setArchiveDisplayProgress] = React.useState(0);
  const archiveProgressAnimRef = React.useRef(0);
  const archiveProgressRafRef = React.useRef(null);
  const [archiveFlight, setArchiveFlight] = React.useState(null);
  const archivePanelRef = React.useRef(null);
  const archivePanelIconRef = React.useRef(null);
  const syncedArchiveFocusRef = React.useRef("");
  const animatedArchiveFlightRef = React.useRef("");
  const archiveFlightTimerRef = React.useRef(0);
  const resolvedTitle = title ?? "";
  const resolvedSubtitle = subtitle ?? "";
  const resolvedWide = wide || location.pathname === "/schedule" || location.pathname === "/teacher-export";
  const useCompactMobileHeader = location.pathname.startsWith("/schedule");
  const useHideMobileHeader = ["/account", "/auditories", "/teacher-export", "/profile", "/users"].some(
    p => location.pathname.startsWith(p)
  );
  const feedbackUrl = "https://t.me/vialontrop";
  const halloweenNavImgRef = React.useRef(null);

  React.useEffect(() => {
    if (!HALLOWEEN_MODE) return;
    const onMove = (e) => {
      const img = halloweenNavImgRef.current;
      if (!img) return;
      const r = img.getBoundingClientRect();
      const x = e.clientX - r.left;
      const y = e.clientY - r.top;
      if (x < 0 || x > r.width || y < 0 || y > r.height) {
        img.style.setProperty("--mx", "-9999px");
        img.style.setProperty("--my", "-9999px");
        return;
      }
      img.style.setProperty("--mx", `${x}px`);
      img.style.setProperty("--my", `${y}px`);
    };
    document.addEventListener("mousemove", onMove);
    return () => document.removeEventListener("mousemove", onMove);
  }, []);

  React.useEffect(() => {
    if (!HALLOWEEN_MODE) return;
    const token = getToken();
    if (!token) return;

    const SCARE_URL = { bats: "/scares/bats.js", spiders: "/scares/spiders.js", clowns: "/scares/clowns.js" };
    const SCARE_FN  = { bats: "batSwarm", spiders: "spiderScare", clowns: "clownScare" };
    const CONSENT_URL = "/scares/scare-consent.js";
    const loaded = {};

    const loadScript = (src) => {
      if (loaded[src]) return Promise.resolve();
      return new Promise((resolve, reject) => {
        const s = document.createElement("script");
        s.src = src;
        s.async = true;
        s.onload = () => { loaded[src] = true; resolve(); };
        s.onerror = reject;
        document.body.appendChild(s);
      });
    };

    const postConsent = async (optOut) => {
      try {
        await fetch("/candy/scare-opt-out", {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ opt_out: optOut }),
        });
      } catch { /* ignore */ }
    };

    const askConsentOnLogin = async () => {
      try {
        const r = await fetch("/candy/scare-consent", { headers: { Authorization: `Bearer ${token}` } });
        if (!r.ok) return;
        const consent = await r.json();
        if (consent.asked) return;
        await loadScript(CONSENT_URL);
        if (typeof window.scareConsent !== "function") return;
        const ok = await window.scareConsent({ force: true, remember: false });
        await postConsent(!ok);
      } catch (e) { console.warn("[scare] consent err:", e); }
    };

    const triggerScare = async (type) => {
      const url = SCARE_URL[type] || SCARE_URL.bats;
      const fn = SCARE_FN[type] || SCARE_FN.bats;
      try {
        await loadScript(url);
        if (typeof window[fn] === "function") {
          window[fn]({ sound: true, volume: 0.9 });
        } else {
          console.warn("[scare] fn not found:", fn);
        }
      } catch (e) {
        console.warn("[scare] load failed", e);
      }
    };

    askConsentOnLogin();

    let stopped = false;
    const poll = async () => {
      try {
        const r = await fetch("/candy/pending-scare", { headers: { Authorization: `Bearer ${token}` } });
        if (!r.ok) return;
        const data = await r.json();
        if (data?.pending && !stopped) {
          triggerScare(data.scare_type);
        }
      } catch { /* ignore */ }
    };

    poll();
    const iv = setInterval(poll, 15000);
    const onFocus = () => poll();
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      stopped = true;
      clearInterval(iv);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, []);

  const navItem =
    "inline-flex h-11 items-center justify-center gap-2 rounded-full px-4 text-base font-semibold transition duration-200 sm:text-sm text-slate-600 dark:text-slate-300";
  const activeNav = HALLOWEEN_MODE
  ? "text-[#ffb347] dark:text-[#ffb347] bg-[rgba(255,179,71,0.15)] dark:bg-[rgba(255,179,71,0.2)] shadow-[inset_0_0_0_1px_rgba(255,179,71,0.4)] dark:shadow-[inset_0_0_0_1px_rgba(255,179,71,0.3)]"
  : "bg-[#eef7fc] dark:bg-[rgba(55,57,66,0.30)] text-[#229ED9] dark:text-blue-300 shadow-[inset_0_0_0_1px_rgba(191,227,247,0.8)] dark:shadow-[inset_0_0_0_1px_rgba(96,165,250,0.25)]";
  const utilityButton =
    "inline-flex items-center justify-center rounded-full border transition duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2";

  const darkWallpaperSymbols = [
    { symbol: "○", top: "8%", left: "4%", size: "92px", rotate: "-8deg", opacity: 0.07, color: "#60a5fa" },
    { symbol: "≋", top: "21%", left: "12%", size: "104px", rotate: "-10deg", opacity: 0.07, color: "#34d399" },
    { symbol: "✶", top: "36%", left: "5%", size: "98px", rotate: "6deg", opacity: 0.06, color: "#a78bfa" },
    { symbol: "♯", top: "50%", left: "14%", size: "112px", rotate: "-12deg", opacity: 0.07, color: "#60a5fa" },
    { symbol: "◇", top: "66%", left: "7%", size: "94px", rotate: "-7deg", opacity: 0.06, color: "#8899b5" },
    { symbol: "∿", top: "82%", left: "13%", size: "106px", rotate: "10deg", opacity: 0.06, color: "#38bdf8" },
    { symbol: "≡", top: "9%", right: "5%", size: "96px", rotate: "8deg", opacity: 0.07, color: "#60a5fa" },
    { symbol: "◎", top: "23%", right: "13%", size: "108px", rotate: "6deg", opacity: 0.06, color: "#34d399" },
    { symbol: "♯", top: "38%", right: "6%", size: "102px", rotate: "11deg", opacity: 0.06, color: "#a78bfa" },
    { symbol: "∫", top: "53%", right: "14%", size: "118px", rotate: "-10deg", opacity: 0.06, color: "#566480" },
    { symbol: "§", top: "68%", right: "7%", size: "114px", rotate: "-8deg", opacity: 0.06, color: "#38bdf8" },
    { symbol: "∞", top: "83%", right: "12%", size: "110px", rotate: "-9deg", opacity: 0.06, color: "#34d399" },
  ];
  const shortFio = React.useMemo(() => {
    const parts = user?.fio?.split(" ").filter(Boolean) ?? [];
    if (!parts.length) return "";
    const surname = parts[0];
    const initials = parts
      .slice(1, 3)
      .map((part) => `${part[0]?.toUpperCase() || ""}.`)
      .join("");
    return initials ? `${surname} ${initials}` : surname;
  }, [user?.fio]);

  const role = user?.role; // "admin" | "teacher" | что-то ещё

  /* Teacher mobile nav: long-press on Аудитории swaps to Участники */
  const canTeacherSwap = role === "teacher";
  const [teacherTabSwap, setTeacherTabSwap] = React.useState(() => {
    try { return localStorage.getItem("teacherTabSwap") === "1"; } catch { return false; }
  });
  const [swapHintOpen, setSwapHintOpen] = React.useState(false);
  const longPressTimerRef = React.useRef(null);
  const longPressFiredRef = React.useRef(false);

  const toggleTeacherSwap = React.useCallback(() => {
    setTeacherTabSwap(v => {
      const next = !v;
      try { localStorage.setItem("teacherTabSwap", next ? "1" : "0"); } catch {}
      return next;
    });
    setSwapHintOpen(false);
  }, []);

  const handleLongPressStart = React.useCallback(() => {
    if (!canTeacherSwap) return;
    longPressFiredRef.current = false;
    if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
    longPressTimerRef.current = setTimeout(() => {
      longPressFiredRef.current = true;
      setSwapHintOpen(true);
      try { navigator.vibrate?.(15); } catch {}
      longPressTimerRef.current = null;
    }, 450);
  }, [canTeacherSwap]);

  const handleLongPressCancel = React.useCallback(() => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  }, []);

  const handleSwapAnchorClick = React.useCallback((e) => {
    if (longPressFiredRef.current) {
      e.preventDefault();
      e.stopPropagation();
      longPressFiredRef.current = false;
    }
  }, []);

  React.useEffect(() => {
    if (!swapHintOpen) return;
    const close = (e) => {
      const t = e.target;
      if (!t?.closest?.("[data-teacher-swap-anchor]") && !t?.closest?.("[data-teacher-swap-chip]")) {
        setSwapHintOpen(false);
      }
    };
    document.addEventListener("touchstart", close, { passive: true });
    document.addEventListener("mousedown", close);
    return () => {
      document.removeEventListener("touchstart", close);
      document.removeEventListener("mousedown", close);
    };
  }, [swapHintOpen]);

  // 🔹 Меню по ролям
  const menuItems = [];

  if (role === "admin") {
    menuItems.push(
      { to: "/", label: "📊 Панель" },
      { to: "/auditories", label: "🏫 Аудитории" },
      { to: "/schedule", label: "🗓 Расписание" },
      { to: "/teacher-export", label: "📤 Нагрузка" }
    );
  } else if (role === "teacher") {
    menuItems.push(
      { to: "/auditories", label: "🏫 Аудитории" },
      { to: "/schedule", label: "🗓 Расписание" },
      { to: "/teacher-export", label: "📤 Нагрузка" },
      { to: "/users", label: "👥 Участники" }
    );
  } else if (user) {
    menuItems.push(
      { to: "/auditories", label: "🏫 Аудитории" },
      { to: "/schedule", label: "🗓 Расписание" },
      { to: "/users", label: "👥 Участники" }
    );
  } else {
    // Для неавторизованных гостей
    menuItems.push({ to: "/schedule", label: "🗓 Расписание" });
  }

  const mobileBottomNavItems = React.useMemo(
  () => {
    const items = [];

    if (canTeacherSwap && teacherTabSwap) {
      items.push({ to: "/users", label: "Участники", icon: "users", swappable: true });
    } else {
      items.push({ to: "/auditories", label: "Аудитории", icon: "rooms", swappable: canTeacherSwap });
    }
    items.push({ to: "/schedule", label: "Расписание", icon: "schedule" });

    // Добавляем "Нагрузка" только для преподавателей и админов
    if (ready && user && (user.role === "teacher" || user.role === "admin" || user.role === "superadmin")) {
      items.push({ to: "/teacher-export", label: "Нагрузка", icon: "export" });
    }

    if (ready && user && user.role !== "teacher" && user.role !== "admin" && user.role !== "superadmin") {
      items.push({ to: "/users", label: "Участники", icon: "users" });
    }

    items.push({
      to: ready && user ? "/account" : "/login",
      label: "Профиль",
      icon: "account",
      useAvatar: Boolean(ready && user),
    });

    return items;
  },
  [ready, user, canTeacherSwap, teacherTabSwap]
);

  const wallpaperSymbols = [
    { symbol: "○", top: "8%", left: "4%", size: "92px", rotate: "-8deg", opacity: 0.12, color: "#64748b" },
    { symbol: "≋", top: "21%", left: "12%", size: "104px", rotate: "-10deg", opacity: 0.12, color: "#0f766e" },
    { symbol: "✶", top: "36%", left: "5%", size: "98px", rotate: "6deg", opacity: 0.11, color: "#8b5cf6" },
    { symbol: "♯", top: "50%", left: "14%", size: "112px", rotate: "-12deg", opacity: 0.12, color: "#2563eb" },
    { symbol: "◇", top: "66%", left: "7%", size: "94px", rotate: "-7deg", opacity: 0.11, color: "#64748b" },
    { symbol: "∿", top: "82%", left: "13%", size: "106px", rotate: "10deg", opacity: 0.1, color: "#0891b2" },
    { symbol: "≡", top: "9%", right: "5%", size: "96px", rotate: "8deg", opacity: 0.12, color: "#0369a1" },
    { symbol: "◎", top: "23%", right: "13%", size: "108px", rotate: "6deg", opacity: 0.11, color: "#0f766e" },
    { symbol: "♯", top: "38%", right: "6%", size: "102px", rotate: "11deg", opacity: 0.11, color: "#4f46e5" },
    { symbol: "∫", top: "53%", right: "14%", size: "118px", rotate: "-10deg", opacity: 0.1, color: "#334155" },
    { symbol: "§", top: "68%", right: "7%", size: "114px", rotate: "-8deg", opacity: 0.11, color: "#0284c7" },
    { symbol: "∞", top: "83%", right: "12%", size: "110px", rotate: "-9deg", opacity: 0.1, color: "#0f766e" },
  ];
  const activeWallpaper = isDark ? darkWallpaperSymbols : wallpaperSymbols;

  React.useEffect(() => {
    const syncArchiveJob = () => {
      const jobId = localStorage.getItem(ARCHIVE_IMPORT_JOB_KEY);
      if (!jobId) {
        setArchiveImportClosing(false);
        setArchiveImportJob(null);
        return;
      }
      let snapshot = null;
      try {
        snapshot = JSON.parse(localStorage.getItem(ARCHIVE_IMPORT_JOB_SNAPSHOT_KEY) || "null");
      } catch {
        snapshot = null;
      }
      setArchiveImportHidden(false);
      setArchiveImportClosing(false);
      setArchiveImportJob((prev) => {
        if (prev && prev.job_id === jobId) return prev;
        if (snapshot && snapshot.job_id === jobId) return snapshot;
        return {
          job_id: jobId,
          source: snapshot?.source || "",
          status: "queued",
          current_step: "Подключаемся к фоновой задаче",
          progress_current: 0,
          progress_total: 0,
          logs: [],
          files: [],
          archives: [],
          error_message: "",
        };
      });
    };

    syncArchiveJob();
    window.addEventListener("storage", syncArchiveJob);
    window.addEventListener("archive-import-job-changed", syncArchiveJob);
    return () => {
      window.removeEventListener("storage", syncArchiveJob);
      window.removeEventListener("archive-import-job-changed", syncArchiveJob);
    };
  }, []);

  React.useEffect(() => {
    if (!ready || !user || !archiveImportJob?.job_id) return undefined;

    let cancelled = false;
    const poll = async () => {
      try {
        const res = await fetch(`${API}/schedule/archive-upload/${archiveImportJob.job_id}`, {
          headers: {
            ...getAuthHeaders(),
          },
        });

        if (res.status === 404) {
          localStorage.removeItem(ARCHIVE_IMPORT_JOB_KEY);
          localStorage.removeItem(ARCHIVE_IMPORT_JOB_SNAPSHOT_KEY);
          window.dispatchEvent(new CustomEvent("archive-import-job-changed", { detail: { jobId: "" } }));
          if (!cancelled) setArchiveImportJob(null);
          return;
        }

        const payload = await res.json().catch(() => null);
        if (!res.ok || !payload) return;
        localStorage.setItem(ARCHIVE_IMPORT_JOB_SNAPSHOT_KEY, JSON.stringify(payload));
        if (!cancelled) setArchiveImportJob(payload);

      } catch {
        return;
      }
    };

    poll();
    const intervalMs =
      archiveImportJob.status === "success" || archiveImportJob.status === "error" ? 10000 : 2000;
    const timer = window.setInterval(poll, intervalMs);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [archiveImportJob?.job_id, archiveImportJob?.status, ready, user]);

  React.useEffect(() => {
    const handleExternalMenuToggle = () => {
      setMenuOpen((prev) => !prev);
    };
    window.addEventListener("audassist:toggle-mobile-menu", handleExternalMenuToggle);
    return () => {
      window.removeEventListener("audassist:toggle-mobile-menu", handleExternalMenuToggle);
    };
  }, []);

  React.useEffect(() => {
    const handleArchiveUpdated = () => {
      invalidateCachedJson("schedule:archive-list:");
      invalidateCachedJson("schedule:week:");
    };

    window.addEventListener("audassist:archive-updated", handleArchiveUpdated);
    return () => {
      window.removeEventListener("audassist:archive-updated", handleArchiveUpdated);
    };
  }, []);

  React.useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  React.useEffect(() => {
    window.dispatchEvent(
      new CustomEvent("audassist:mobile-menu-state", {
        detail: { open: menuOpen },
      })
    );
  }, [menuOpen]);

  const archiveProgressLabel = React.useMemo(() => {
    if (!archiveImportJob) return "";
    const current = Number(archiveImportJob.progress_current || 0);
    const total = Number(archiveImportJob.progress_total || 0);
    if (!total) return "";
    return `${Math.min(current, total)}/${total}`;
  }, [archiveImportJob]);

  const archiveProgressPercent = React.useMemo(() => {
    if (!archiveImportJob) return 0;
    if (archiveImportJob.status === "success") return 100;
    const current = Number(archiveImportJob.progress_current || 0);
    const total = Number(archiveImportJob.progress_total || 0);
    if (!total) return archiveImportJob.status === "error" ? 100 : 8;
    return Math.max(8, Math.min(100, Math.round((current / total) * 100)));
  }, [archiveImportJob]);

  React.useEffect(() => {
    setArchiveDisplayProgress(0);
  }, [archiveImportJob?.job_id]);

  React.useEffect(() => {
    if (archiveProgressRafRef.current) {
      cancelAnimationFrame(archiveProgressRafRef.current);
      archiveProgressRafRef.current = null;
    }
    if (!archiveImportJob) {
      archiveProgressAnimRef.current = 0;
      setArchiveDisplayProgress(0);
      return undefined;
    }

    const target = archiveProgressPercent;
    const status = archiveImportJob.status;
    let lastTs = null;

    const tick = (ts) => {
      const dt = lastTs === null ? 16 : Math.min(ts - lastTs, 100);
      lastTs = ts;
      const scale = dt / 120;

      const prev = archiveProgressAnimRef.current;
      let next = prev;

      if (status === "success") {
        if (prev < 100) next = Math.min(100, prev + Math.max(1.6, (100 - prev) * 0.28) * scale);
      } else if (prev < target) {
        next = Math.min(target, prev + Math.max(0.8, (target - prev) * 0.24) * scale);
      } else if (status === "running" || status === "queued") {
        const headroom =
          target < 20 ? 11 :
          target < 40 ? 9 :
          target < 60 ? 7 :
          target < 80 ? 5 : 3;
        const virtualCap = Math.min(98, target + headroom);
        if (prev < virtualCap) next = Math.min(virtualCap, prev + 0.14 * scale);
      }

      if (next !== prev) {
        archiveProgressAnimRef.current = next;
        if (Math.round(next) !== Math.round(prev)) {
          setArchiveDisplayProgress(next);
        }
      }

      archiveProgressRafRef.current = requestAnimationFrame(tick);
    };

    archiveProgressRafRef.current = requestAnimationFrame(tick);
    return () => {
      if (archiveProgressRafRef.current) {
        cancelAnimationFrame(archiveProgressRafRef.current);
        archiveProgressRafRef.current = null;
      }
    };
  }, [archiveImportJob, archiveProgressPercent]);

  const archiveStatusText =
    archiveImportJob?.status === "error"
      ? "Ошибка импорта"
      : archiveImportJob?.status === "success"
      ? "Импорт завершён"
      : "Импортируется в фоне";

  const archivePanelTone =
    archiveImportJob?.status === "error"
      ? "border-rose-200 bg-[linear-gradient(180deg,rgba(255,255,255,0.97),rgba(255,241,242,0.94))]"
      : archiveImportJob?.status === "success"
      ? "border-emerald-200 bg-[linear-gradient(180deg,rgba(255,255,255,0.98),rgba(236,253,245,0.95))]"
      : "border-emerald-200 bg-[linear-gradient(180deg,rgba(255,255,255,0.97),rgba(240,253,244,0.94))]";

  const archiveSourceLabel = React.useMemo(
    () => normalizeArchiveSourceLabel(archiveImportJob?.source),
    [archiveImportJob?.source]
  );
  const archiveDisplayPercent = Math.round(archiveDisplayProgress);

  const playArchiveFlight = React.useCallback(({ fromX, fromY, status = "success", id }) => {
    const targetNode = findVisibleScheduleNavTarget();
    if (!targetNode || !Number.isFinite(fromX) || !Number.isFinite(fromY)) return false;

    const targetRect = targetNode.getBoundingClientRect();
    const toX = targetRect.left + targetRect.width / 2;
    const toY = targetRect.top + targetRect.height / 2;
    const flightId = String(id || `manual:${Date.now()}`);

    if (archiveFlightTimerRef.current) {
      window.clearTimeout(archiveFlightTimerRef.current);
    }

    setArchiveFlight({
      id: flightId,
      fromX,
      fromY,
      deltaX: toX - fromX,
      deltaY: toY - fromY,
      status,
    });

    archiveFlightTimerRef.current = window.setTimeout(() => {
      setArchiveFlight((prev) => (prev?.id === flightId ? null : prev));
      archiveFlightTimerRef.current = 0;
    }, 1560);

    return true;
  }, []);

  React.useEffect(() => {
    if (archiveImportJob?.status !== "success") return undefined;
    const focusPayload = normalizeArchiveFocusPayload(archiveImportJob);
    const syncKey = focusPayload
      ? `${focusPayload.job_id}:${focusPayload.range_ids.join(",")}`
      : `archive-success:${archiveImportJob?.job_id || Date.now()}`;

    if (focusPayload && syncedArchiveFocusRef.current !== syncKey) {
      syncedArchiveFocusRef.current = syncKey;
      localStorage.setItem(ARCHIVE_IMPORT_FOCUS_KEY, JSON.stringify(focusPayload));
      window.dispatchEvent(
        new CustomEvent("archive-import-focus-changed", {
          detail: focusPayload,
        })
      );
    }

    if (animatedArchiveFlightRef.current === syncKey) return undefined;
    const sourceNode = archivePanelIconRef.current || archivePanelRef.current;
    const targetNode = findVisibleScheduleNavTarget();
    if (!sourceNode || !targetNode) return undefined;

    animatedArchiveFlightRef.current = syncKey;
    const sourceRect = sourceNode.getBoundingClientRect();
    const fromX = sourceRect.left + sourceRect.width * 0.28;
    const fromY = sourceRect.top + sourceRect.height * 0.42;
    playArchiveFlight({
      id: syncKey,
      fromX,
      fromY,
      status: archiveImportJob.status,
    });
    return undefined;
  }, [archiveImportJob, playArchiveFlight]);

  React.useEffect(() => () => {
    if (archiveFlightTimerRef.current) {
      window.clearTimeout(archiveFlightTimerRef.current);
      archiveFlightTimerRef.current = 0;
    }
  }, []);

  React.useEffect(() => {
    if (archiveImportJob?.status !== "success" || archiveImportHidden) {
      setArchiveImportClosing(false);
      return undefined;
    }

    const fadeTimer = window.setTimeout(() => {
      setArchiveImportClosing(true);
    }, 1200);

    const hideTimer = window.setTimeout(() => {
      setArchiveImportHidden(true);
      setArchiveImportClosing(false);
      localStorage.removeItem(ARCHIVE_IMPORT_JOB_KEY);
      localStorage.removeItem(ARCHIVE_IMPORT_JOB_SNAPSHOT_KEY);
      window.dispatchEvent(new CustomEvent("archive-import-job-changed", { detail: { jobId: "" } }));
    }, 1800);

    return () => {
      window.clearTimeout(fadeTimer);
      window.clearTimeout(hideTimer);
    };
  }, [archiveImportHidden, archiveImportJob?.status]);

  return (
    <div className="min-h-screen text-gray-900 dark:text-slate-100 font-sans bg-gradient-to-b from-[#eef2f7] via-[#f7f9fc] to-[#ffffff] dark:from-[#1a1b1f] dark:via-[#1e2026] dark:to-[#22242b] relative [overflow-x:clip] ">
      <div className="pointer-events-none fixed inset-0 overflow-hidden z-0" aria-hidden="true">
        {activeWallpaper.map((item, i) => (
          <span
            key={`${item.symbol}-${i}`}
            className="absolute select-none hidden sm:block"
            style={{
              top: item.top,
              left: item.left,
              right: item.right,
              fontSize: item.size,
              opacity: item.opacity,
              transform: `rotate(${item.rotate})`,
              filter: "blur(0.15px)",
              lineHeight: 1,
              fontWeight: 500,
              color: item.color,
              textShadow: "0 2px 10px rgba(15, 23, 42, 0.08)",
            }}
          >
            {item.symbol}
          </span>
        ))}
      </div>

      {/* === ШАПКА === */}
      <header
        className={`sticky top-0 z-50 ${
          useHideMobileHeader ? "hidden sm:block" : ""
        } ${
          useCompactMobileHeader
            ? "border-b-0 bg-transparent shadow-none"
            : "border-b border-slate-200/80 dark:border-slate-700/30 bg-white/94 dark:bg-[rgba(26,27,31,0.94)] backdrop-blur-[4px] shadow-sm sm:border-b-0 sm:bg-transparent sm:shadow-none sm:backdrop-blur-none dark:sm:bg-transparent"
        }`}
      >
        <div
          className={`max-w-6xl mx-auto flex items-center justify-center ${
            useCompactMobileHeader
              ? "px-0 py-0 sm:px-6 sm:py-4"
              : "px-4 py-3 sm:px-6 sm:py-4"
          }`}
        >
          <nav
            className={HALLOWEEN_MODE
              ? "hidden sm:relative sm:flex sm:w-full sm:max-w-[1100px] sm:flex-row sm:items-center sm:justify-center sm:gap-6 sm:px-4 sm:py-2 sm:bg-transparent sm:rounded-[22px] sm:border-0"
              : "hidden sm:flex sm:w-auto sm:flex-row sm:items-center sm:gap-3 sm:rounded-full sm:border sm:border-slate-200/80 dark:sm:border-slate-700/40 sm:bg-white/88 dark:sm:bg-[rgba(26,27,31,0.88)] sm:px-1.5 sm:py-1.5 sm:shadow-[0_12px_32px_-22px_rgba(15,23,42,0.26)] dark:sm:shadow-[0_12px_32px_-22px_rgba(0,0,0,0.55)] sm:backdrop-blur-[8px]"}
            style={HALLOWEEN_MODE ? { aspectRatio: "2004 / 120" } : undefined}
          >
            {HALLOWEEN_MODE && (
              <img
                ref={halloweenNavImgRef}
                src={isDark ? "/navigation_bar_pc_animated.svg" : "/navigation_bar_pc_light_animated.svg"}
                alt=""
                aria-hidden="true"
                className="pointer-events-none absolute left-0 top-0 w-full select-none"
                style={{
                  aspectRatio: "2004 / 432",
                  zIndex: 0,
                  "--mx": "-9999px",
                  "--my": "-9999px",
                  WebkitMaskImage:
                    "radial-gradient(circle at var(--mx) var(--my), transparent 0, transparent 55px, #000 90px), linear-gradient(to bottom, #000 0, #000 28%, transparent 28%)",
                  maskImage:
                    "radial-gradient(circle at var(--mx) var(--my), transparent 0, transparent 55px, #000 90px), linear-gradient(to bottom, #000 0, #000 28%, transparent 28%)",
                  WebkitMaskComposite: "source-over",
                  maskComposite: "add",
                }}
              />
            )}
            <div className="relative z-10 hidden sm:flex sm:flex-row sm:items-center sm:gap-1">
              {menuItems.map((item) => {
                const meta = MENU_META[item.to] || {
                  title: item.label,
                  icon: "default",
                };

                return (
                  <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={() => setMenuOpen(false)}
                  data-nav-target={item.to === "/schedule" ? "schedule" : undefined}
                  className={({ isActive }) => {
                    const active = (item.to === "/" && location.pathname === "/") || (item.to !== "/" && isActive);
                    return `${navItem} ${
                      // для главной учитываем только ровно "/"
                      active
                        ? activeNav
                        : "text-slate-600 dark:text-slate-300 hover:bg-slate-100/80 dark:hover:bg-slate-700/40 hover:text-slate-900 dark:hover:text-slate-100"
                    }`
                  }}
                >
                  {({ isActive }) => {
                    const active = (item.to === "/" && location.pathname === "/") || (item.to !== "/" && isActive);
                    return (
                      <>
                        <span
                        className={`inline-flex ${HALLOWEEN_MODE ? "h-7 w-7" : "h-4 w-4"} shrink-0 items-center justify-center ${
                          HALLOWEEN_MODE
                            ? (active ? "text-[#ffb347] dark:text-[#ffb347]" : "text-slate-700 dark:text-slate-300")
                            : (active ? "text-[#229ED9]" : "text-slate-500")
                        }`}
                          style={HALLOWEEN_MODE && active ? {
                            filter: "drop-shadow(0 0 6px rgba(255,146,40,0.95)) drop-shadow(0 0 12px rgba(255,120,20,0.75)) drop-shadow(0 0 20px rgba(255,100,10,0.55))"
                          } : undefined}
                        >
                          <MenuGlyph name={meta.icon} className={HALLOWEEN_MODE ? "h-7 w-7" : "h-4 w-4"} />
                        </span>
                        <span
                          className="whitespace-nowrap tracking-[0.01em]"
                          style={HALLOWEEN_MODE && active ? { textShadow: "0 0 6px rgba(255,146,40,0.9), 0 0 12px rgba(255,120,20,0.6)" } : undefined}
                        >
                          {meta.title}
                        </span>
                      </>
                    );
                  }}
                </NavLink>
                );
              })}
            </div>

            <div className="relative z-10 hidden sm:flex sm:flex-row sm:items-center sm:justify-end sm:gap-2">
              <a
                href={feedbackUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setMenuOpen(false)}
                className="group inline-flex h-11 items-center justify-center gap-2 rounded-full border border-slate-200 dark:border-slate-700/60 bg-white/78 dark:bg-[#2a2c32]/70 px-4 text-sm font-semibold text-slate-700 dark:text-slate-300 transition duration-200 hover:border-[#bfe3f7] dark:hover:border-blue-600/40 hover:bg-[#f4fbfe] dark:hover:bg-[#32343b] hover:text-[#229ED9] dark:hover:text-blue-400 focus:outline-none focus:ring-2 focus:ring-[#229ED9]/20 focus:ring-offset-2"
                aria-label="Обратная связь"
                title="Обратная связь"
              >
                <span className="inline-flex h-4 w-4 items-center justify-center text-slate-500 transition duration-200 group-hover:text-[#229ED9]">
                  <MenuGlyph name="feedback" className="h-4 w-4" />
                </span>
                <span className="relative z-10 hidden sm:inline tracking-[0.01em]">Обратная связь</span>
              </a>

              {!ready ? (
                <div className="h-9" />
              ) : user ? (
                <div className="flex flex-col sm:flex-row items-center justify-center sm:justify-start gap-2">
                  <NavLink
                    to="/account"
                    onClick={() => setMenuOpen(false)}
                    className={`${utilityButton} h-11 gap-2 px-2.5 py-1.5 text-sm font-medium text-slate-700 dark:text-slate-300 bg-white/78 dark:bg-[#2a2c32]/70 border-slate-200 dark:border-slate-700/60 hover:border-[#bfe3f7] dark:hover:border-blue-600/40 hover:text-[#229ED9] dark:hover:text-blue-400 hover:bg-[#f4fbfe] dark:hover:bg-[#32343b] focus:ring-[#229ED9]/20`}
                  >
                    {/* Аватар в шапке */}
                    <div className="relative w-8 h-8 flex-shrink-0">
                      <div className="w-full h-full rounded-full bg-[#eef7fc] dark:bg-[rgba(55,57,66,0.35)] flex items-center justify-center overflow-hidden border border-[#d6ebf7] dark:border-blue-600/30 text-[11px] font-semibold text-[#229ED9] dark:text-blue-400">
                        {user.avatar_url ? (
                          <img
                            src={resolveAvatarSrc(user.avatar_url)}
                            alt="Аватар"
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                        ) : (
                          <span className="text-xs">
                            {user.fio
                              ?.split(" ")
                              .filter(Boolean)
                              .map((p) => p[0])
                              .join("")
                              .slice(0, 2)
                              .toUpperCase() || "👨‍🏫"}
                          </span>
                        )}
                      </div>
                      {user.avatar_frame && !(navIsCrownHolder && navCrownEquipped) && (
                        <img
                          src={user.avatar_frame}
                          alt=""
                          className="absolute pointer-events-none select-none"
                          style={{ inset: 0, width: "100%", height: "100%", zIndex: 10, transform: `translateX(${user.avatar_frame_offset_x ?? 0}%) translateY(${user.avatar_frame_offset_y ?? 0}%) scale(${(user.avatar_frame_scale ?? 100) / 100})`, transformOrigin: 'center' }}
                        />
                      )}
                      {navIsCrownHolder && navCrownEquipped && (
                        <img src="/badges/golden_crown.svg" alt="" className="pointer-events-none select-none" style={{ position: 'absolute', top: -14, left: '50%', transform: 'translateX(-50%)', width: 22, height: 22, objectFit: 'contain', zIndex: 30 }} />
                      )}
                    </div>
                    <span className="hidden sm:inline max-w-[190px] truncate">{shortFio}</span>
                  </NavLink>

                  <button
                    onClick={() => {
                      logout();
                      setMenuOpen(false);
                    }}
                    className={`${utilityButton} h-11 w-11 border-slate-200 dark:border-slate-700/60 bg-white/78 dark:bg-[#2a2c32]/70 text-slate-500 dark:text-slate-400 hover:border-[#bfe3f7] dark:hover:border-blue-600/40 hover:bg-[#f4fbfe] dark:hover:bg-[#32343b] hover:text-[#229ED9] dark:hover:text-blue-400 focus:ring-[#229ED9]/20`}
                    aria-label="Выйти"
                    title="Выйти"
                  >
                    <MenuGlyph name="logout" className="h-[18px] w-[18px]" />
                  </button>
                </div>
              ) : (
                // Находим место, где выводится кнопка "🔐 Войти"
                  <NavLink
                    to="/login"
                    onClick={() => setMenuOpen(false)}
                    className={({ isActive }) =>
                      `${navItem} ${
                        isActive ? activeNav : "text-slate-600 hover:bg-slate-100/80 hover:text-slate-900"
                      }`
                    }
                  >
                    <span className="inline-flex h-4 w-4 shrink-0 items-center justify-center text-slate-500">
                      <MenuGlyph name="login" className="h-4 w-4" />
                    </span>
                    <span className="whitespace-nowrap tracking-[0.01em]">Войти</span>
                  </NavLink>
              )}
            </div>
          </nav>
        </div>
      </header>

      {/* === ОСНОВНОЙ КОНТЕНТ === */}
      <main
        className={`${
          resolvedWide
            ? "w-full px-2 sm:px-6"
            : "max-w-5xl mx-auto px-3 sm:px-6"

        } py-8 pb-28 ${useCompactMobileHeader ? "sm:pt-3 sm:pb-10" : "sm:py-10 sm:pb-10"} animate-fade-in-up relative`}
      >
        {resolvedTitle && (
          <h2 className="text-2xl sm:text-3xl font-bold mb-2 text-center sm:text-left">
            {resolvedTitle}
          </h2>
        )}
        {resolvedSubtitle && (
          <p className="text-gray-600 mb-4 sm:mb-6 text-center sm:text-left">
            {resolvedSubtitle}
          </p>
        )}
        {children}
      </main>

      {createPortal(
      <nav
  className="fixed inset-x-3 bottom-3 z-[65] sm:hidden"
  aria-label="Основная навигация"
  style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
>
  <div
    className={HALLOWEEN_MODE
    ? `relative grid gap-1 items-end rounded-[22px] overflow-hidden p-1.5 bg-transparent border-0 ${
    mobileBottomNavItems.length === 2 ?  "grid-cols-2 " :
    mobileBottomNavItems.length === 3 ?  "grid-cols-3 " :  "grid-cols-4 "
    }`
    : `grid gap-1 rounded-[22px] border border-white/80 dark:border-slate-700/40 bg-[linear-gradient(180deg,rgba(255,255,255,0.98),rgba(244,247,251,0.96))] dark:bg-[linear-gradient(180deg,rgba(34,36,40,0.98),rgba(26,27,31,0.97))] p-1.5 shadow-[0_24px_60px_-28px_rgba(15,23,42,0.38)] dark:shadow-[0_24px_60px_-28px_rgba(0,0,0,0.6)] backdrop-blur-[10px] ${
    mobileBottomNavItems.length === 2 ?  "grid-cols-2 " :
    mobileBottomNavItems.length === 3 ?  "grid-cols-3 " :  "grid-cols-4 "
    }`}
    style={HALLOWEEN_MODE ? { aspectRatio: "384 / 174" } : undefined}
  >
    {HALLOWEEN_MODE && (
      <img
        src={isDark ? "/navigation_bar.svg" : "/navigation_bar_ph_light_animated.svg"}
        alt=""
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 w-full h-full select-none"
        style={{ objectFit: "fill", zIndex: 0 }}
      />
    )}
    {mobileBottomNavItems.map((item) => {
      const meta =
        MENU_META[item.to] ||
        (item.icon === "account" ? MENU_META["/account"] : null) || {
          title: item.label,
          icon: item.icon,
        };

      const navEl = (
        <NavLink
          to={item.to}
          end
          onClick={(e) => { handleSwapAnchorClick(e); setMenuOpen(false); }}
          onTouchStart={item.swappable ? handleLongPressStart : undefined}
          onTouchEnd={item.swappable ? handleLongPressCancel : undefined}
          onTouchMove={item.swappable ? handleLongPressCancel : undefined}
          onTouchCancel={item.swappable ? handleLongPressCancel : undefined}
          onContextMenu={item.swappable ? (e) => { e.preventDefault(); setSwapHintOpen(true); } : undefined}
          data-nav-target={item.to === "/schedule" ? "schedule" : undefined}
          data-teacher-swap-anchor={item.swappable ? "" : undefined}
          className={({ isActive }) =>
            HALLOWEEN_MODE
              ? `group relative z-10 flex min-h-[52px] flex-col items-center justify-end gap-0 rounded-[16px] px-1 pt-1.5 pb-1 text-center transition-all duration-200 [-webkit-tap-highlight-color:transparent] ${
                  isActive
                    ? "text-[#ffb347] dark:text-[#ffb347]"
                    : "text-slate-700 dark:text-slate-300 active:scale-[0.98]"
                }`
              : `group flex min-h-[52px] flex-col items-center justify-center gap-0.5 rounded-[16px] px-1 py-1.5 text-center transition-all duration-200 [-webkit-tap-highlight-color:transparent] ${
                  isActive
                    ? "bg-[linear-gradient(180deg,rgba(235,243,255,0.98),rgba(244,248,255,0.94))] dark:bg-[linear-gradient(180deg,rgba(55,57,66,0.40),rgba(40,42,50,0.2))] text-[#2b6fe8] dark:text-blue-400 shadow-[inset_0_0_0_1px_rgba(191,219,254,0.95)] dark:shadow-[inset_0_0_0_1px_rgba(96,165,250,0.2)]"
                    : "text-slate-500 dark:text-slate-400 active:scale-[0.98]"
                }`
          }
        >
          {({ isActive }) => (
            <>
              {item.useAvatar ? (
                <span className="relative inline-flex h-7 w-7 items-center justify-center">
                  <span
                    className={HALLOWEEN_MODE
                      ? "inline-flex h-7 w-7 items-center justify-center overflow-hidden rounded-full border border-slate-600/50 dark:border-slate-600/50 bg-[rgba(255,255,255,0.1)] dark:bg-[rgba(255,255,255,0.1)] transition"
                      : `inline-flex h-7 w-7 items-center justify-center overflow-hidden rounded-full border transition ${
                          isActive
                            ? "border-blue-200 dark:border-blue-600/40 bg-white dark:bg-[#2a2c32] shadow-[0_6px_12px_-10px_rgba(43,111,232,0.55)]"
                            : "border-slate-200 dark:border-slate-700/50 bg-white/88 dark:bg-[#2a2c32]/60"
                        }`}
                    style={HALLOWEEN_MODE && isActive ? {
                      filter: "drop-shadow(0 0 6px rgba(255,146,40,0.95)) drop-shadow(0 0 12px rgba(255,120,20,0.75)) drop-shadow(0 0 20px rgba(255,100,10,0.55))"
                    } : undefined}
                  >
                    {user?.avatar_url ? (
                      <img
                        src={resolveAvatarSrc(user.avatar_url)}
                        alt="Профиль"
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <MenuGlyph name="account" className="h-4 w-4" />
                    )}
                  </span>
                  {navIsCrownHolder && navCrownEquipped && (
                    <img src="/badges/golden_crown.svg" alt="" className="pointer-events-none select-none absolute" style={{ top: -12, left: '50%', transform: 'translateX(-50%)', width: 20, height: 20, objectFit: 'contain', zIndex: 30 }} />
                  )}
                </span>
              ) : (
                <span
                  className={HALLOWEEN_MODE
                    ? `inline-flex h-8 w-8 items-center justify-center rounded-full transition ${
                        isActive ? "text-[#ffb347] dark:text-[#ffb347]" : "text-slate-700 dark:text-slate-300"
                      }`
                    : `inline-flex h-7 w-7 items-center justify-center rounded-full border transition ${
                        isActive
                          ? "border-blue-200 dark:border-blue-600/40 bg-white dark:bg-[#2a2c32]/80 text-[#2b6fe8] dark:text-blue-400 shadow-[0_6px_12px_-10px_rgba(43,111,232,0.55)]"
                          : "border-transparent bg-transparent text-slate-400 dark:text-slate-500"
                      }`}
                  style={HALLOWEEN_MODE && isActive ? {
                    filter: "drop-shadow(0 0 6px rgba(255,146,40,0.95)) drop-shadow(0 0 12px rgba(255,120,20,0.75)) drop-shadow(0 0 20px rgba(255,100,10,0.55))"
                  } : undefined}
                >
                  <MenuGlyph name={meta.icon || item.icon || "default"} className={HALLOWEEN_MODE ? "h-6 w-6" : "h-4 w-4"} />
                </span>
              )}
              <span
                className={`text-[11px] font-medium leading-tight ${
                  HALLOWEEN_MODE
                    ? (isActive ? "text-[#ffb347] dark:text-[#ffb347]" : "text-slate-700 dark:text-slate-300")
                    : (isActive ? "text-[#2b6fe8] dark:text-blue-400" : "text-slate-500 dark:text-slate-400")
                }`}
                style={HALLOWEEN_MODE && isActive ? { textShadow: "0 0 6px rgba(255,146,40,0.9), 0 0 12px rgba(255,120,20,0.6)" } : undefined}
              >
                {item.label}
              </span>
            </>
          )}
        </NavLink>
      );

      if (!item.swappable) {
        return <React.Fragment key={`mobile-tab-${item.to}`}>{navEl}</React.Fragment>;
      }

      const chipLabel = teacherTabSwap ? "Аудитории" : "Участники";
      const chipIcon  = teacherTabSwap ? "rooms"      : "users";

      return (
        <div key={`mobile-tab-${item.to}`} className="relative z-10">
          {navEl}
          {swapHintOpen && (
            <button
              type="button"
              data-teacher-swap-chip=""
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleTeacherSwap(); }}
              style={{ left: '50%', top: '-44px', transform: 'translateX(-50%)' }}
              className="absolute inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-blue-200 dark:border-blue-600/40 bg-white dark:bg-[#2a2c32] px-3 py-1.5 text-[12px] font-semibold text-[#2b6fe8] dark:text-blue-300 shadow-[0_10px_24px_-12px_rgba(43,111,232,0.5)]"
            >
              <MenuGlyph name={chipIcon} className="h-3.5 w-3.5" />
              {chipLabel}
            </button>
          )}
        </div>
      );
    })}
  </div>
</nav>,
      document.body
      )}

      {/* === ПОДВАЛ === */}

      {archiveFlight ? (
        <div className="pointer-events-none fixed inset-0 z-[82]" aria-hidden="true">
          <div
            className={`absolute flex h-14 w-14 items-center justify-center rounded-[22px] archive-flight-motion ${archiveFlightTone(archiveFlight.status)}`}
            style={{
              left: `${archiveFlight.fromX - 28}px`,
              top: `${archiveFlight.fromY - 28}px`,
              "--archive-flight-x": `${archiveFlight.deltaX}px`,
              "--archive-flight-y": `${archiveFlight.deltaY}px`,
            }}
          >
            <span className="archive-flight-motion__trail" />
            <span className="archive-flight-motion__glow" />
            <ArchiveFileGlyph className="archive-flight-motion__icon h-7 w-7" />
          </div>
        </div>
      ) : null}

      {updateReady && (
        <aside className="fixed right-4 bottom-24 z-[71] sm:bottom-4">
          <div className="flex items-center gap-2.5 rounded-[16px] border border-emerald-200/90 dark:border-emerald-700/40 bg-[linear-gradient(180deg,rgba(255,255,255,0.98),rgba(236,253,245,0.96))] dark:bg-[linear-gradient(180deg,rgba(34,36,40,0.98),rgba(6,40,30,0.96))] px-3.5 py-2.5 shadow-[0_18px_40px_rgba(15,23,42,0.14)] dark:shadow-[0_18px_40px_rgba(0,0,0,0.45)] backdrop-blur-xl">
            <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-[12px] border border-emerald-200 dark:border-emerald-700/40 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400">
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M21 2v6h-6" /><path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
                <path d="M3 22v-6h6" /><path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
              </svg>
            </span>
            <div>
              <div className="text-[13px] font-semibold leading-tight text-slate-900 dark:text-slate-100">Доступно обновление</div>
              <button
                onClick={reload}
                className="text-[11px] font-medium text-emerald-600 dark:text-emerald-400 hover:underline"
              >
                Перезагрузить
              </button>
            </div>
          </div>
        </aside>
      )}

      <ConnectionIssueModal />

      {(!isOnline || cacheUsed) && (
        <aside
          className="fixed left-1/2 -translate-x-1/2 top-3 z-[69] sm:left-auto sm:translate-x-0 sm:right-4 sm:top-auto sm:bottom-4 transition-opacity duration-300 opacity-40 hover:opacity-100 cursor-pointer select-none outline-none [-webkit-tap-highlight-color:transparent]"
          onClick={e => e.currentTarget.classList.toggle('opacity-100')}
        >
          <div className="flex items-center gap-2.5 rounded-[16px] border border-amber-200/90 dark:border-amber-700/35 bg-[linear-gradient(180deg,rgba(255,255,255,0.98),rgba(255,251,235,0.96))] dark:bg-[linear-gradient(180deg,rgba(34,36,40,0.98),rgba(45,30,8,0.96))] px-3.5 py-2.5 shadow-[0_18px_40px_rgba(15,23,42,0.14)] dark:shadow-[0_18px_40px_rgba(0,0,0,0.45)] backdrop-blur-xl">
            <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-[12px] border border-amber-200 dark:border-amber-700/35 bg-amber-50 dark:bg-amber-900/25 text-amber-500 dark:text-amber-400">
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <line x1="1" y1="1" x2="23" y2="23" />
                <path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55" />
                <path d="M5 12.55a10.94 10.94 0 0 1 5.17-2.39" />
                <path d="M10.71 5.05A16 16 0 0 1 22.56 9" />
                <path d="M1.42 9a15.91 15.91 0 0 1 4.7-2.88" />
                <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
                <line x1="12" y1="20" x2="12.01" y2="20" />
              </svg>
            </span>
            <div>
              <div className="text-[13px] font-semibold leading-tight text-slate-900 dark:text-slate-100">Нет подключения</div>
              <div className="text-[11px] text-slate-500 dark:text-slate-400">
                {cacheTs ? `данные от ${formatCacheTime(cacheTs)}` : cacheUsed ? "данные из кеша" : "кеш недоступен"}
              </div>
            </div>
          </div>
        </aside>
      )}

      {archiveImportJob && !archiveImportHidden ? (
        <aside className={`fixed right-4 bottom-24 z-[70] w-[min(262px,calc(100vw-1.5rem))] transition-all duration-500 sm:bottom-4 ${
          archiveImportClosing ? "translate-y-2 opacity-0" : "translate-y-0 opacity-100"
        }`}>
          <div ref={archivePanelRef} className={`rounded-[20px] border px-3.5 py-3 shadow-[0_18px_40px_rgba(15,23,42,0.14)] dark:shadow-[0_18px_40px_rgba(0,0,0,0.5)] backdrop-blur-xl transition-all duration-300 ${archivePanelTone}`}>
            <div className="flex items-start gap-3">
              <div
                ref={archivePanelIconRef}
                className={`mt-0.5 inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-[16px] border shadow-[0_12px_26px_-18px_rgba(22,101,52,0.45)] ${
                  archiveImportJob.status === "error"
                    ? "border-rose-200 bg-rose-50 text-rose-500"
                    : archiveImportJob.status === "success"
                    ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                    : "border-emerald-200 bg-[linear-gradient(180deg,#f7fff8,#dcfce7)] text-emerald-700"
                }`}
              >
                <ArchiveFileGlyph className="h-6 w-6" />
              </div>

              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="truncate text-[13px] font-semibold leading-tight text-slate-900 dark:text-slate-100">
                      {archiveSourceLabel}: {archiveStatusText}
                    </div>
                    <div className="mt-0.5 truncate text-[10px] text-slate-500 dark:text-slate-400">
                      {archiveImportJob.current_step || "Ожидаем обновление"}
                    </div>
                  </div>

                  <div className="shrink-0 text-right">
                    <div className="text-base font-semibold leading-none text-emerald-800 dark:text-emerald-300">
                      {archiveDisplayPercent}%
                    </div>
                    {archiveProgressLabel ? (
                      <div className="mt-1 text-[9px] font-medium text-slate-400">
                        {archiveProgressLabel}
                      </div>
                    ) : null}
                  </div>
                </div>

                <div className="mt-2.5 h-1.5 overflow-hidden rounded-full bg-emerald-100/90">
                  <div
                    className={`h-full rounded-full transition-[width] duration-150 ${
                      archiveImportJob.status === "error"
                        ? "bg-rose-500"
                        : archiveImportJob.status === "success"
                        ? "bg-emerald-600"
                        : "bg-emerald-500"
                    }`}
                    style={{ width: `${archiveDisplayPercent}%` }}
                  />
                </div>
              </div>

              {(archiveImportJob.status === "success" || archiveImportJob.status === "error") ? (
                <button
                  type="button"
                  onClick={() => {
                    setArchiveImportHidden(true);
                    localStorage.removeItem(ARCHIVE_IMPORT_JOB_KEY);
                    localStorage.removeItem(ARCHIVE_IMPORT_JOB_SNAPSHOT_KEY);
                  }}
                  className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-slate-400 dark:text-slate-500 transition hover:bg-slate-100 dark:hover:bg-slate-700/50 hover:text-slate-700 dark:hover:text-slate-300"
                  aria-label="Скрыть импорт архива"
                >
                  ×
                </button>
              ) : null}
            </div>
          </div>
        </aside>
      ) : null}
    </div>
  );
}
